import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppAuth } from "@/context/AppAuthContext";
import { appPin } from "@/lib/appPin";
import { appBio } from "@/lib/appBio";
import { toast } from "sonner";
import { Lock, Delete, Fingerprint } from "lucide-react";

export default function AppLock({ onUnlock }) {
  const { session, logout } = useAppAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const temPin = appPin.isSet();
  const temBio = appBio.isSet(session?.app_user?.id);
  const [bioBusy, setBioBusy] = useState(false);

  const usarBio = useCallback(async () => {
    setBioBusy(true);
    try {
      await appBio.verificar();
      onUnlock();
    } catch (_) {
      toast.error(temPin ? "Biometria não confirmada — tente de novo ou use o PIN" : "Biometria não confirmada — toque para tentar de novo");
    } finally { setBioBusy(false); }
  }, [onUnlock, temPin]);

  // Abre o leitor de digital / Face ID assim que o app abre
  useEffect(() => {
    if (temBio) usarBio();
  }, []);  // eslint-disable-line

  useEffect(() => {
    if (pin.length === 4) {
      (async () => {
        try {
          await appPin.verify(pin);
          onUnlock();
        } catch (err) {
          setShake(true);
          setTimeout(() => setShake(false), 400);
          toast.error(err.message);
          setPin("");
          // Se zerou todas tentativas, força login
          if (!appPin.isSet()) {
            logout();
            navigate("/app/login");
          }
        }
      })();
    }
  }, [pin]);  // eslint-disable-line

  const press = (d) => { if (pin.length < 4) setPin(pin + d); };
  const back = () => setPin(pin.slice(0, -1));

  const sair = () => {
    logout(); appPin.clear(); appBio.clear();
    navigate("/app/login");
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-white" data-testid="app-lock">
      <div className="h-16 w-16 rounded-full bg-emerald-900/60 grid place-items-center mb-3">
        <Lock size={28} className="text-emerald-400" />
      </div>
      <div className="text-lg font-bold">Olá, {session?.funcionario?.nome?.split(" ")[0] || "você"}</div>
      <div className="text-xs text-slate-500 mb-6">{temPin ? (temBio ? "Use a biometria ou digite seu PIN" : "Digite seu PIN para entrar") : "Use a digital ou o Face ID para entrar"}</div>

      {temBio && (
        <button onClick={usarBio} disabled={bioBusy} data-testid="btn-lock-biometria"
          className="mb-6 h-14 px-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 font-bold inline-flex items-center gap-2 disabled:opacity-60">
          <Fingerprint size={22} /> {bioBusy ? "Aguardando…" : "Desbloquear com biometria"}
        </button>
      )}

      {temPin && (<>
      <div className={`flex gap-3 mb-8 ${shake ? "animate-shake" : ""}`}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i}
            className={`h-4 w-4 rounded-full border-2 transition
              ${i < pin.length ? "bg-emerald-500 border-emerald-500" : "border-slate-600"}`} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
        {[1,2,3,4,5,6,7,8,9].map((n) => (
          <button key={n} onClick={() => press(String(n))}
            data-testid={`pin-key-${n}`}
            className="h-16 rounded-xl bg-slate-800 hover:bg-slate-700 text-2xl font-bold">
            {n}
          </button>
        ))}
        <div />
        <button onClick={() => press("0")} data-testid="pin-key-0"
          className="h-16 rounded-xl bg-slate-800 hover:bg-slate-700 text-2xl font-bold">0</button>
        <button onClick={back} data-testid="pin-key-back"
          className="h-16 rounded-xl bg-slate-800 hover:bg-slate-700 grid place-items-center">
          <Delete size={20} />
        </button>
      </div>
      </>)}

      <button onClick={sair} data-testid="btn-lock-sair"
        className="mt-8 text-xs text-slate-500 hover:text-slate-300">
        Usar senha completa / Sair
      </button>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0) }
          25% { transform: translateX(-6px) }
          75% { transform: translateX(6px) }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
